import { Avatar, Icon, SelectOutlined } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { EmployeeOption } from '../hooks/useKeyResultResponsibles';

interface KeyResultResponsibleSelectProps {
    options: EmployeeOption[];
    value?: string;
    onChange: (employeeId: string | undefined) => void;
    dataTestId: string;
}

/** The dropdown shows eight rows before it scrolls; past that, finding someone needs a search. */
const MAX_UNSEARCHED_OPTIONS = 8;

/** Wide enough for "Aucun responsable" and most full names on one line, under a narrower pill. */
const MENU_MIN_WIDTH = 256;

/** The 24px avatar sits 4px inside the 32px pill, concentric with its round end; the smaller
 * search glyph needs twice that. The trigger's base utilities win over `triggerClassName`, not
 * over its style. */
const PILL_STYLE = { borderRadius: 9999, paddingRight: 12 };
const LEADING_INSET = { avatar: 4, glyph: 8 };

/** Not an employee id, which are server UUIDs: picking it hands the key result back to no one. */
const NO_RESPONSIBLE = '';

/**
 * The compact pill a team key result is handed to someone with, in the card's header: the person's
 * avatar and name once chosen, a "Responsable" call to action until then. Only one responsible is
 * kept per key result, so the list is a single choice with a way back to none.
 */
export default function KeyResultResponsibleSelect({
    options,
    value,
    onChange,
    dataTestId,
}: KeyResultResponsibleSelectProps) {
    const { t } = useTranslation();
    const responsible = options.find((option) => option.id === value);

    const entries = options.map((option) => ({
        id: option.id,
        value: option.id,
        label: option.label,
        startIcon: (
            <Avatar
                src={option.avatarUrl}
                alt={''}
                initials={option.initials}
                size={'4xs'}
            />
        ),
    }));
    const removal = responsible
        ? [
              {
                  id: 'no-responsible',
                  value: NO_RESPONSIBLE,
                  label: t('objectives.form.noResponsible'),
                  startIcon: (
                      <Icon
                          name={'users-user-remove'}
                          size={'sm'}
                          color={'inherit'}
                          ariaHidden
                      />
                  ),
              },
          ]
        : undefined;

    return (
        <div className={'max-w-[220px]'}>
            <SelectOutlined
                size={'sm'}
                options={entries}
                footerOptions={removal}
                value={value ?? NO_RESPONSIBLE}
                onChange={(employeeId) => onChange(employeeId || undefined)}
                placeholder={t('objectives.form.responsible')}
                // Nobody to hand it to yet: the people are still on their way or not chosen.
                disabled={options.length === 0}
                searchable={options.length > MAX_UNSEARCHED_OPTIONS}
                searchPlaceholder={t('objectives.searchEmployee')}
                emptyText={t('objectives.form.noEmployeesFound')}
                showSelectedStartIcon
                leadingIcon={
                    <Icon
                        name={'users-user-search'}
                        size={'sm'}
                        colorClassName={'text-obj-muted'}
                        ariaHidden
                    />
                }
                menuMinWidth={MENU_MIN_WIDTH}
                // A label replaces the trigger's text as its accessible name: it carries the chosen person too.
                ariaLabel={
                    responsible
                        ? t('objectives.form.responsibleOf', { name: responsible.label })
                        : t('objectives.form.responsible')
                }
                placement={'bottom-right'}
                triggerStyle={{
                    ...PILL_STYLE,
                    paddingLeft: LEADING_INSET[responsible ? 'avatar' : 'glyph'],
                }}
                // The mockup's 12px, which the select's own text size does not offer: the override
                // has to reach its label, the span after the icon's.
                triggerClassName={'[&>span:last-of-type]:text-[12px]'}
                triggerBackgroundClassName={'bg-white/70'}
                triggerBorderClassName={'border border-obj-line'}
                // Replaces the outlined variant's own `hover:border-select-outline`.
                triggerHoverBorderClassName={'hover:border-primary'}
                textClassName={'text-navy'}
                dataTestId={dataTestId}
            />
        </div>
    );
}
